import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCreditUnitsByDistrictQuery } from '@api/services/lookup/hooks';

const debounce = (func, wait) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const CreditUnitTableCell = ({ getValue, row, column, table, cell, district }) => {
  const { data: lookupTableCreditUnits = [] } = useCreditUnitsByDistrictQuery(district, !!district);
  const columnMeta = column.columnDef.meta;
  const tableMeta = table.options.meta;
  const initialValue = getValue();
  const initialOptions = columnMeta?.options[0];
  const [value, setValue] = useState(initialValue);
  const [creditUnitOptions, setCreditUnitOptions] = useState(initialOptions);
  const mitigationType = row.getValue('mitigationType');
  const debouncedUpdateRef = useRef();

  useEffect(() => {
    debouncedUpdateRef.current = debounce((newValue) => {
      const processedValue = columnMeta?.type === 'number' ? Number(newValue) : (newValue ?? newValue);
      if (tableMeta?.updateData) {
        tableMeta.updateData(row.index, column.id, processedValue);
      }
    }, 500);
  }, [row.index, column.id, tableMeta?.updateData, columnMeta?.type, tableMeta]);

  const updateValue = useCallback((newValue) => {
    debouncedUpdateRef.current(newValue);
  }, []);

  useEffect(() => {
    switch (mitigationType) {
      case 'Mitigation Bank':
        setCreditUnitOptions(
          lookupTableCreditUnits
            ?.filter((item) => item.mitbank === true)
            .map((item) => ({ value: item.creditUnitName, label: item.creditUnitName }))
        );
        return;
      case 'In-lieu Fee':
        setCreditUnitOptions(
          lookupTableCreditUnits
            ?.filter((item) => item.inLieu === true)
            .map((item) => ({ value: item.creditUnitName, label: item.creditUnitName }))
        );
        return;
      case 'Advanced Permittee Responsible Mitigation':
        setCreditUnitOptions(
          lookupTableCreditUnits
            ?.filter((item) => item.advancePermitteeResponsible === true)
            .map((item) => ({ value: item.creditUnitName, label: item.creditUnitName }))
        );
        return;
      default:
        setCreditUnitOptions(initialOptions);
        return;
    }
  }, [mitigationType, lookupTableCreditUnits, initialOptions, setCreditUnitOptions]);

  const handleBlur = () => {};

  const handleChange = (e) => {
    setValue(e.target.value);
    updateValue(e.target.value);
  };

  return (
    <select
      aria-label={'Credit Units'}
      disabled={columnMeta?.readOnly || (mitigationType !== 'Mitigation Bank' && mitigationType !== 'In-lieu Fee')}
      id={cell.id}
      onBlur={handleBlur}
      onChange={handleChange}
      style={{
        width: '100%',
        borderColor: 'hsl(0, 0%, 80%)',
        cursor:
          columnMeta?.readOnly || (mitigationType !== 'Mitigation Bank' && mitigationType !== 'In-lieu Fee')
            ? 'not-allowed'
            : 'auto',
      }}
      value={value ?? ''}
    >
      <option key={0} value='' className='none' style={{ display: 'none' }}>
        -- Select a value --
      </option>
      {creditUnitOptions?.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

export default CreditUnitTableCell;
